import { isSpellbookError, type SpellbookErrorSource } from "@sdk/spellbook";

import { AuthenticationRequiredError, isAccessRedirectError } from "./access";

// Temporary bridge for legacy access/auth callers that bypass Spellbook's error callback.
// Remove this handler and its setter once those callers use the generated SDK error path.
// Registration keeps the legacy helpers independent of Sentry and router initialization.
let accessErrorHandler: ((error: unknown, source: SpellbookErrorSource) => void) | undefined;

/**
 * Registers the central policy for legacy callers before API work starts.
 * Remove this setter together with accessErrorHandler when those callers migrate to Spellbook.
 */
export function setAccessErrorHandler(
    handler: (error: unknown, source: SpellbookErrorSource) => void,
): void {
    accessErrorHandler = handler;
}

/**
 * Temporary entry point for legacy callers to request central authentication recovery
 * without importing instrumentation. Remove with the registration bridge once those
 * callers use Spellbook's error callback. This function does not throw.
 */
export function redirectForAuthenticationRequired(): void {
    accessErrorHandler?.(new AuthenticationRequiredError(), "call");
}

/** For legacy catches, forwards access errors with their source and consumes SDK errors. */
export function redirectIfAccessError(
    error: unknown,
    source: SpellbookErrorSource = "call",
): boolean {
    // Spellbook errors have reached the central policy, so do not handle them again here.
    if (isSpellbookError(error)) return true;
    if (!isAccessRedirectError(error)) return false;

    accessErrorHandler?.(error, source);
    return true;
}
